import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Moon, Sparkles, ArrowRight } from 'lucide-react';

const zodiacSigns = [
  { name: 'Aries', symbol: '♈', crystal: 'Carnelian', color: '#FF6B6B' },
  { name: 'Taurus', symbol: '♉', crystal: 'Rose Quartz', color: '#00C853' },
  { name: 'Gemini', symbol: '♊', crystal: 'Citrine', color: '#FFB800' },
  { name: 'Cancer', symbol: '♋', crystal: 'Moonstone', color: '#C0C0C0' },
  { name: 'Leo', symbol: '♌', crystal: 'Tiger Eye', color: '#FF9F43' },
  { name: 'Virgo', symbol: '♍', crystal: 'Amazonite', color: '#00B4D8' },
  { name: 'Libra', symbol: '♎', crystal: 'Lapis Lazuli', color: '#3B82F6' },
  { name: 'Scorpio', symbol: '♏', crystal: 'Black Obsidian', color: '#1e1919' },
  { name: 'Sagittarius', symbol: '♐', crystal: 'Turquoise', color: '#40E0D0' },
  { name: 'Capricorn', symbol: '♑', crystal: 'Garnet', color: '#8B0000' },
  { name: 'Aquarius', symbol: '♒', crystal: 'Amethyst', color: '#9D4EDD' },
  { name: 'Pisces', symbol: '♓', crystal: 'Aquamarine', color: '#7FDBFF' },
];

const AstrologySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSign, setActiveSign] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    const section = document.getElementById('astrology-section');
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="astrology-section" className="py-24 px-6 bg-gradient-to-b from-[#faf8f5] to-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-20 right-20 text-[200px] text-[#9D4EDD]/5 font-serif">✦</div>
        <div className="absolute bottom-20 left-20 text-[150px] text-[#FFB800]/5 font-serif">☽</div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Moon className="w-5 h-5 text-[#9D4EDD]" />
            <span className="text-sm uppercase tracking-[0.3em] text-[#c9a96e] font-bold">Cosmic Alignment</span>
            <Star className="w-5 h-5 text-[#FFB800] fill-current" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A2E] mb-6">Crystals & Your <span className="text-gradient-vibrant">Zodiac</span></h2>
          <p className="text-lg text-[#4A4A6A] max-w-2xl mx-auto">Discover which gemstones resonate with your celestial energy and amplify your natural strengths.</p>
        </div>

        <div className={`mb-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-wrap justify-center gap-4">
            {zodiacSigns.map((sign, idx) => (
              <button key={sign.name} onMouseEnter={() => setActiveSign(sign)} onMouseLeave={() => setActiveSign(null)}
                className={`relative p-4 rounded-xl transition-all duration-300 ${activeSign?.name === sign.name ? 'bg-white shadow-xl scale-110 z-10' : 'bg-white/50 hover:bg-white hover:shadow-lg'}`}
                style={{ transitionDelay: `${idx * 50}ms` }}>
                <div className="text-3xl mb-1" style={{ color: sign.color }}>{sign.symbol}</div>
                <span className="text-xs text-[#61525a] font-medium">{sign.name}</span>
                {activeSign?.name === sign.name && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 bg-[#1e1919] text-white p-3 rounded-xl shadow-xl text-center z-20">
                    <p className="text-[#c9a96e] text-xs uppercase tracking-wider mb-1">Power Crystal</p>
                    <p className="font-serif text-lg">{sign.crystal}</p>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-[#1e1919]" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className={`grid md:grid-cols-3 gap-6 mb-12 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-br from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-2xl p-6 border border-[#9D4EDD]/20">
            <Star className="w-8 h-8 text-[#FFB800] fill-current mb-4" />
            <h3 className="font-serif text-xl text-[#1e1919] mb-3">Birth Chart Crystals</h3>
            <p className="text-sm text-[#61525a]">Your sun sign is just the beginning. Moon and rising signs also influence which crystals serve you best.</p>
          </div>
          <div className="bg-gradient-to-br from-[#FFB800]/10 to-[#00C853]/10 rounded-2xl p-6 border border-[#FFB800]/20">
            <Moon className="w-8 h-8 text-[#9D4EDD] mb-4" />
            <h3 className="font-serif text-xl text-[#1e1919] mb-3">Lunar Cycles</h3>
            <p className="text-sm text-[#61525a]">New moons for intentions, full moons for charging, waning moons for releasing.</p>
          </div>
          <div className="bg-gradient-to-br from-[#00B4D8]/10 to-[#9D4EDD]/10 rounded-2xl p-6 border border-[#00B4D8]/20">
            <Sparkles className="w-8 h-8 text-[#FF6B9D] mb-4" />
            <h3 className="font-serif text-xl text-[#1e1919] mb-3">Planetary Transits</h3>
            <p className="text-sm text-[#61525a]">Mercury retrograde calls for communication crystals. Venus transits for heart-opening stones.</p>
          </div>
        </div>

        <div className={`text-center transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Link to="/hub" className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-4 rounded-xl hover:shadow-xl transition-all">
            <Star className="w-5 h-5" />
            <span className="font-semibold tracking-wider text-sm uppercase">Daily Cosmic Guidance</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AstrologySection;
